import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { registerUser } from '@/lib/api';
import { motion } from 'framer-motion';
import { Feather, ArrowLeft, Eye, EyeOff } from 'lucide-react';

export function Register() {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        // Password Complexity Check
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);

        if (!hasUpperCase || !hasLowerCase || !hasNumber) {
            setError('パスワードは、大文字・小文字・数字をそれぞれ1文字以上含める必要があります。');
            setIsLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('パスワードが一致しません。');
            setIsLoading(false);
            return;
        }

        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            await registerUser({ username, email, password, timezone });
            setSuccess(true);
        } catch (err: any) {
            console.error(err);
            setError(err.response?.data?.detail || '登録処理中にエラーが発生しました。');
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif p-4 relative overflow-hidden">
                <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1541701494587-cb58502866ab?q=80&w=2670&auto=format&fit=crop')] bg-cover bg-center"></div>

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="w-full max-w-lg bg-[#1c1917] border border-[#d4af37] p-10 text-center relative z-10 shadow-2xl"
                >
                    <Feather className="mx-auto text-[#d4af37] mb-6" size={48} />
                    <h2 className="text-3xl text-[#fcd34d] mb-4 font-bold tracking-widest">確認メールを送信しました</h2>
                    <div className="h-px w-16 bg-[#d4af37] mx-auto my-6 opacity-50"></div>
                    <p className="text-[#d4af37]/80 mb-6 leading-relaxed">
                        入居手続きの最終段階です。<br />
                        ご登録いただいたメールアドレス ({email}) に<br />
                        認証用のリンクをお送りしました。
                    </p>
                    <div className="bg-[#292524] p-4 border border-[#d4af37]/30 text-sm text-[#d4af37]/60 mb-8">
                        ※開発環境の場合: サーバーのコンソール画面を確認して、認証URLをクリックしてください。
                    </div>
                    <Button
                        onClick={() => navigate('/login')}
                        className="bg-transparent border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#1c1917] uppercase tracking-widest"
                    >
                        門へ戻る
                    </Button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 z-0 opacity-30 bg-[url('https://images.unsplash.com/photo-1518135714426-c18f5ffb6f4d?q=80&w=2696&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay"></div>

            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="w-full max-w-md p-1 relative z-10"
            >
                <div className="bg-[#1c1917]/95 border border-[#d4af37]/50 shadow-[0_0_50px_rgba(0,0,0,0.8)] p-8 text-[#d4af37] backdrop-blur-sm">

                    <div className="mb-8 flex items-center relative">
                        <div className="absolute left-0 cursor-pointer text-[#d4af37]/60 hover:text-[#d4af37] transition-colors" onClick={() => navigate('/login')}>
                            <ArrowLeft size={20} />
                        </div>
                        <h2 className="w-full text-center text-2xl font-bold tracking-[0.2em] text-[#fcd34d]">RESIDENT REGISTRATION</h2>
                    </div>

                    <p className="text-center text-[#d4af37]/60 text-xs mb-8 tracking-widest">
                        新たな居住者として契約を結びます。<br />
                        虚偽の申告は固く禁じられています。
                    </p>

                    <form className="space-y-6" onSubmit={handleRegister}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">入居希望者名</label>
                                <Input
                                    type="text"
                                    className="bg-[#292524] border-[#d4af37]/30 text-[#fcd34d] h-11 focus:ring-[#d4af37]"
                                    placeholder="希望ユーザー名"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">Email Address</label>
                                <Input
                                    type="email"
                                    className="bg-[#292524] border-[#d4af37]/30 text-[#fcd34d] h-11 focus:ring-[#d4af37]"
                                    placeholder="メールアドレス"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">パスワード</label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        className="bg-[#292524] border-[#d4af37]/30 text-[#fcd34d] h-11 focus:ring-[#d4af37] pr-10"
                                        placeholder="独自のパスコードを設定"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]/50 hover:text-[#d4af37]"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">パスワード（確認）</label>
                                <div className="relative">
                                    <Input
                                        type={showConfirmPassword ? "text" : "password"}
                                        className="bg-[#292524] border-[#d4af37]/30 text-[#fcd34d] h-11 focus:ring-[#d4af37] pr-10"
                                        placeholder="独自のパスコードを再入力"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]/50 hover:text-[#d4af37]"
                                    >
                                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="text-red-400 text-xs text-center bg-red-950/30 p-2 border border-red-900/50">
                                {error}
                            </div>
                        )}

                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 group"
                        >
                            <Feather size={16} className="group-hover:rotate-45 transition-transform" />
                            {isLoading ? 'Processing...' : '契約書に署名する'}
                        </motion.button>
                    </form>
                </div>
            </motion.div>
        </div>
    );
}
